import { PRICING_MODELS, USE_CASES, BLOG_CATEGORIES } from "@/lib/api";
import { ICONS } from "@/components/site/CategoryIcon";

const SEO = { title: "SEO metadata", fields: [
  { k: "seo_title", label: "SEO title", type: "text", hint: "Up to ~60 characters. Defaults to the page title." },
  { k: "seo_description", label: "Meta description", type: "textarea", rows: 2, hint: "Up to ~160 characters." },
] };
const PUBLISH = { title: "Publishing", fields: [
  { k: "status", type: "publish" },
  { k: "author", label: "Author", type: "author", hint: "Manage authors under Authors." },
] };

export const SCHEMAS = {
  software: {
    label: "Software", singular: "software listing", titleKey: "name", publicPath: "/software/",
    defaults: { status: "draft", author: "SaaSPivora Editorial Team", key_features: [], pros: [], cons: [], use_cases: [], best_use_cases: [], limitations: [], alternatives: [], faqs: [] },
    sections: [
      { title: "Basics", fields: [
        { k: "name", label: "Software name", type: "text", required: true },
        { k: "slug", label: "URL slug", type: "text", hint: "Leave blank to generate from the name." },
        { k: "category", label: "Category", type: "category", required: true },
        { k: "logo_url", label: "Official product logo", type: "image" },
        { k: "short_description", label: "Short factual description", type: "textarea", rows: 2 },
      ] },
      { title: "Review content", fields: [
        { k: "overview", label: "Product overview", type: "markdown" },
        { k: "key_features", label: "Key features", type: "lines" },
        { k: "pros", label: "Pros", type: "lines" },
        { k: "cons", label: "Cons", type: "lines" },
        { k: "use_cases", label: "Business use cases (directory filter)", type: "checks", options: USE_CASES },
        { k: "best_use_cases", label: "Practical use cases", type: "lines" },
        { k: "ideal_users", label: "Ideal users / business types", type: "lines" },
        { k: "integrations", label: "Integrations", type: "lines" },
        { k: "limitations", label: "Limitations", type: "lines" },
        { k: "alternatives", label: "Alternatives", type: "software" },
        { k: "faqs", label: "FAQs", type: "faqs" },
        { k: "screenshots", label: "Screenshots", type: "screenshots", hint: "Use screenshots you captured from your own account or images from the vendor's official press/media kit, and credit the source." },
        { k: "conclusion", label: "Editorial conclusion", type: "markdown", hint: "The page shows as a full review only when every required section is filled and sources are recorded; otherwise it is shown as a labelled product profile." },
      ] },
      { title: "Sources & fact-check", fields: [
        { k: "sources", label: "Sources (official pages used)", type: "sources" },
        { k: "facts_checked_at", label: "Facts last checked", type: "date" },
      ] },
      { title: "Pricing", fields: [
        { k: "pricing_model", label: "Pricing model", type: "select", options: [["", "Not specified"], ...PRICING_MODELS.map((p) => [p.value, p.label])] },
        { k: "pricing_summary", label: "Pricing summary", type: "textarea", rows: 2, hint: "Shown publicly only with a source page + checked date, or a verified date." },
        { k: "pricing_source_url", label: "Official pricing page URL", type: "text" },
        { k: "pricing_checked_at", label: "Pricing taken from official page on", type: "date" },
        { k: "pricing_verified_at", label: "Pricing confirmed by an editor on", type: "date" },
      ] },
      { title: "Links & flags", fields: [
        { k: "website_url", label: "Official website URL", type: "text" },
        { k: "affiliate_url", label: "Affiliate link (optional)", type: "text", hint: "When set, outbound buttons use this link, marked rel=sponsored, and an affiliate disclosure is shown." },
        { k: "featured", label: "Feature on homepage", type: "checkbox" },
        { k: "editor_verified", label: "Owner has reviewed this listing for accuracy", type: "checkbox" },
      ] },
      PUBLISH, SEO,
    ],
  },
  articles: {
    label: "Articles", singular: "article", titleKey: "title", publicPath: "/blog/",
    defaults: { status: "draft", author: "SaaSPivora Editorial Team", blog_category: "SaaS Guides", article_type: "guide", related_software: [] },
    sections: [
      { title: "Article", fields: [
        { k: "title", label: "Title", type: "text", required: true },
        { k: "slug", label: "URL slug", type: "text", hint: "Leave blank to generate from the title." },
        { k: "blog_category", label: "Blog category", type: "select", options: BLOG_CATEGORIES.map((c) => [c, c]) },
        { k: "article_type", label: "Content type", type: "select", options: [["guide", "Guide"], ["review", "Review"], ["comparison", "Comparison"], ["news", "News"]] },
        { k: "excerpt", label: "Excerpt", type: "textarea", rows: 2 },
        { k: "cover_image", label: "Cover image", type: "image" },
        { k: "cover_alt", label: "Cover image alt text", type: "text" },
        { k: "cover_credit", label: "Image credit / licence", type: "text", hint: "e.g. Photo via Unsplash (Unsplash License). Only use images you have the right to publish." },
        { k: "cover_credit_url", label: "Credit / licence link", type: "text" },
        { k: "content", label: "Content (Markdown)", type: "markdown", rows: 18, upload: true },
        { k: "related_software", label: "Software mentioned", type: "software" },
        { k: "has_affiliate_links", label: "Contains affiliate links (shows disclosure)", type: "checkbox" },
      ] },
      PUBLISH, SEO,
    ],
  },
  comparisons: {
    label: "Comparisons", singular: "comparison", titleKey: "title", publicPath: "/comparisons/",
    defaults: { status: "draft", author: "SaaSPivora Editorial Team", software: [], feature_rows: [], items: [], key_differences: [] },
    sections: [
      { title: "Comparison", fields: [
        { k: "title", label: "Title", type: "text", required: true, hint: "e.g. HubSpot CRM vs Pipedrive" },
        { k: "slug", label: "URL slug", type: "text" },
        { k: "software", label: "Software compared (2–4, in column order)", type: "software", max: 4 },
        { k: "intro", label: "Introduction", type: "markdown", rows: 4 },
        { k: "feature_rows", label: "Feature table", type: "feature-rows" },
        { k: "items", label: "Pricing, ideal customer, strengths & weaknesses", type: "compare-items" },
        { k: "key_differences", label: "Key differences", type: "lines" },
        { k: "recommendation", label: "Editorial recommendation", type: "markdown", rows: 4 },
        { k: "faqs", label: "FAQs", type: "faqs" },
      ] },
      PUBLISH, SEO,
    ],
  },
  categories: {
    label: "Categories", singular: "category", titleKey: "name", publicPath: "/categories/",
    defaults: { icon: "LayoutGrid", order: 0 },
    sections: [
      { title: "Category", fields: [
        { k: "name", label: "Name", type: "text", required: true },
        { k: "slug", label: "URL slug", type: "text" },
        { k: "description", label: "Description", type: "textarea", rows: 3 },
        { k: "icon", label: "Icon", type: "select", options: Object.keys(ICONS).map((i) => [i, i]) },
        { k: "order", label: "Display order", type: "number" },
      ] },
      SEO,
    ],
  },
  authors: {
    label: "Authors", singular: "author", titleKey: "name", publicPath: "/about#", defaults: {},
    sections: [{ title: "Author", fields: [
      { k: "name", label: "Display name", type: "text", required: true },
      { k: "slug", label: "Slug", type: "text" },
      { k: "title", label: "Role / title", type: "text" },
      { k: "bio", label: "Short bio", type: "textarea", rows: 3, hint: "Only real people or teams. Do not invent credentials." },
    ] }],
  },
  pages: {
    label: "Legal pages", singular: "page", titleKey: "title", publicPath: "/legal/", noCreate: true, noDelete: true,
    defaults: {},
    sections: [
      { title: "Page", fields: [
        { k: "title", label: "Title", type: "text", required: true },
        { k: "content", label: "Content (Markdown)", type: "markdown", rows: 22 },
        { k: "owner_reviewed", label: "I have reviewed this policy (removes the 'template' notice)", type: "checkbox" },
      ] },
      SEO,
    ],
  },
};
